import { useTranslation } from 'react-i18next';

import { apiGet } from '@/api/client';
import type { AdminOverview } from '@/api/types';
import { AsyncSection } from '@/app/components/AsyncSection';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { StatTile } from '@/app/components/ui/StatTile';
import { useAsync } from '@/lib/useAsync';

function formatCurrency(amount: number, currency: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

function formatPercent(rate: number, locale: string): string {
  return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 1 }).format(rate);
}

/**
 * "Beheer › Overzicht" (PLAN-FASE3.md WP-05, "Besluiten (2026-09-22)" scope
 * verkleind): the platform-KPI cards this WP's scope reduces to — omzet,
 * actieve-bedrijven/-gebruikers-tellingen and a retentiecijfer, all derived
 * from `GET /admin/overview`. Deliberately **no** NPS or AI-samenvatting
 * card: no databron for either (no survey system, no AI integration in this
 * API), left out entirely rather than stubbed with fake data — see the
 * API's `api_admin_overview.php` class docblock for the exact definitions.
 * Platform-staff-only, same shape as Beheer › Support: the API 403s anyone
 * who is not a platform admin regardless of what the menu offers to click.
 */
export function AdminOverviewPage() {
  const { t, i18n } = useTranslation();

  const overview = useAsync(() => apiGet('/admin/overview') as Promise<AdminOverview>, []);

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader title={t('manageOverview.page.title')} lead={t('manageOverview.page.lead')} />

      <AsyncSection state={overview} label={t('manageOverview.page.title')} skeleton="card">
        {(data) => {
          const revenue = data.revenue_this_month;
          const retention = data.retention;
          const hasRetentionData = (retention?.companies_last_month ?? 0) > 0;

          return (
            <div className="flex flex-wrap gap-4">
              <StatTile
                label={t('manageOverview.page.activeCompanies')}
                value={data.active_companies_count ?? '—'}
              />
              <StatTile
                label={t('manageOverview.page.activeUsers')}
                value={data.active_users_count ?? '—'}
              />
              <StatTile
                label={t('manageOverview.page.revenueThisMonth')}
                value={
                  revenue && typeof revenue.amount === 'number' && revenue.currency
                    ? formatCurrency(revenue.amount, revenue.currency, i18n.language)
                    : '—'
                }
              />
              <StatTile
                label={t('manageOverview.page.retention')}
                value={
                  hasRetentionData && typeof retention?.rate === 'number'
                    ? formatPercent(retention.rate, i18n.language)
                    : t('manageOverview.page.retentionUnavailable')
                }
                {...(hasRetentionData ? {} : { className: 'opacity-70' })}
              />
            </div>
          );
        }}
      </AsyncSection>
    </section>
  );
}
